import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import DataTable from "react-data-table-component";
import noticeManager from "../apis/notice";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";

const CustomPagination = ({ page, totalRows, rowsPerPage, onChangePage }) => {
    const pages = Math.ceil(totalRows / rowsPerPage);
    const pageNumbers = [];

    for (let i = 1; i <= pages; i++) {
        pageNumbers.push(i);
    }

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "20px 0",
            }}
        >
            <button
                onClick={() => onChangePage(1)}
                disabled={page === 1}
                style={{ margin: "0 5px" }}
            >
                {"<<"}
            </button>
            <button
                onClick={() => onChangePage(page - 1)}
                disabled={page === 1}
                style={{ margin: "0 5px" }}
            >
                {"<"}
            </button>
            {pageNumbers.map((number) => (
                <button
                    key={number}
                    onClick={() => onChangePage(number)}
                    style={{
                        margin: "0 5px",
                        padding: "5px 10px",
                        backgroundColor:
                            page === number ? "#7386E8" : "transparent",
                        color: page === number ? "#fff" : "#000",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                    }}
                >
                    {number}
                </button>
            ))}
            <button
                onClick={() => onChangePage(page + 1)}
                disabled={page === pages}
                style={{ margin: "0 5px" }}
            >
                {">"}
            </button>
            <button
                onClick={() => onChangePage(pages)}
                disabled={page === pages}
                style={{ margin: "0 5px" }}
            >
                {">>"}
            </button>
        </div>
    );
};

const NoticeList = () => {
    const rowsPerPage = 10;
    const [adminCheck, setAdminCheck] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [data, setData] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const { page } = location.state || {};

    const { data: list, isLoading } = useQuery({
        queryKey: ["readNotice"],
        queryFn: noticeManager.getNotice,
    });

    useEffect(() => {
        if (list && list.notice && !isLoading) {
            setData(list.notice);
            setAdminCheck(list.user.is_admin);
        }
    }, [list]);

    useEffect(() => {
        if (page) {
            setCurrentPage(page);
        }
    }, []);
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const paginationComponentOptions = useMemo(
        () => ({
            rowsPerPageText: "Rows per page",
            rangeSeparatorText: "of",
            noRowsPerPage: true,
        }),
        []
    );

    const handleRowClick = (row) => {
        const index = currentData.indexOf(row);
        const number = (currentPage - 1) * rowsPerPage + index + 1;
        navigate(`/notice-detail/${row.id}`, {
            state: { row: row, page: currentPage, admin: adminCheck, rowIndex: number },
        });
    };
    const columns = [
        {
            name: "글번호",
            selector: (row, index) =>
                (currentPage - 1) * rowsPerPage + index + 1,
            sortable: false,
            width: "100px",
        },
        {
            name: "날짜",
            selector: (row) => moment(row.createdAt).format("YYYY-MM-DD"),
            sortable: true,
            width: "250px",
        },
        { name: "제목", selector: (row) => row.title, sortable: false, width: "auto"},
        { name: "작성자", selector: () => "이너뷰", sortable: false, width: "200px" },
        {
            name: "조회수",
            selector: (row) => row?.read_count,
            sortable: false,
            width: "200px",
        },
    ];
    const customStyles = {
        headRow: {
            style: {
                height: "44px",
            },
        },
        headCells: {
            style: {
                background: "rgb(239, 241, 250)",
                color: "rgb(93, 94, 103)",
                fontSize: "13px",
                fontWeight: 500,
                justifyContent: "center",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                borderBottom: "1px solid rgb(239, 241, 250)",
            },
        },
        cells: {
            style: {
                padding: "15px 0",
                justifyContent: "center",
                fontWeight: 400,
                fontSize: "14px",
                letterSpacing: "-0.01em",
                color: "#282B39",
            },
        },
    };

    const goToWritePage = () => {
        navigate("/notice-write", {
            state: { page: page },
        });
    };

    const currentData = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return data.slice(startIndex, endIndex);
    }, [currentPage, data]);

    return (
        <NoticePageContainer>
            <NoticeTitle>공지사항</NoticeTitle>
            <NoticeBodyContainer>
                <DataTable
                    columns={columns}
                    data={currentData}
                    onRowClicked={handleRowClick}
                    pagination
                    paginationServer
                    paginationTotalRows={data.length}
                    paginationPerPage={rowsPerPage}
                    paginationComponentOptions={paginationComponentOptions}
                    onChangePage={handlePageChange}
                    customStyles={customStyles}
                    paginationComponent={(props) => (
                        <CustomPagination
                            page={currentPage}
                            totalRows={data.length}
                            rowsPerPage={rowsPerPage}
                            onChangePage={handlePageChange}
                        />
                    )}
                    highlightOnHover
                    responsive
                    noDataComponent={
                        <div
                            style={{
                                display: "flex",
                                padding: "20px",
                                color: "gray",
                                height: "450px",
                                alignItems: "center",
                            }}
                        >
                            표시할 데이터가 없습니다.
                        </div>
                    }
                />
                {adminCheck && (
                    <NoticeFooterContainer>
                        <WriteBtn onClick={goToWritePage}>작성하기</WriteBtn>
                    </NoticeFooterContainer>
                )}
            </NoticeBodyContainer>
        </NoticePageContainer>
    );
};

const NoticePageContainer = styled.div`
    margin-top: 15px;
    padding: 72px;
    height: 100vh;
    min-width: 1390px;
    min-height: 500px;
    background-color: rgb(248, 249, 249);
`;

const NoticeTitle = styled.p`
    font-weight: 500;
    font-size: 24px;
    line-height: 32px;
    color: #282B39;
`;

const NoticeBodyContainer = styled.div`
    width: 100%;
    margin-top: 30px;
    height: 450px;
`;

const NoticeFooterContainer = styled.div`
    display: flex;
    position: relative;
    bottom: 52px;
    float: right;
`;

const WriteBtn = styled.button`
    transition: 0.2s ease-in-out;
    cursor: pointer;
    height: 40px;
    font-size: 15px;
    border: 1px solid #7386E8;
    background-color: #7386E8;
    border-radius: 50px;
    color: rgb(255, 255, 255);
    width: 100px;
    font-weight: 600;
`;

export default NoticeList;
